import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { publicAgreementAPI } from '../../services/agreementAPI';
import BookingPage from './BookingPage';
import './BookingNDAGate.css';

type GateStatus = 'loading' | 'needs_signature' | 'pass_through';

const BookingNDAGate: React.FC = () => {
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get('email') || '';

  const [gateStatus, setGateStatus] = useState<GateStatus>('loading');
  const [alreadySigned, setAlreadySigned] = useState(false);
  const [ndaContent, setNdaContent] = useState<{ title: string; content: string; version: string } | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState(emailParam);
  const [company, setCompany] = useState('');
  const [address, setAddress] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const sigRef = useRef<SignatureCanvas>(null);
  const sigWrapperRef = useRef<HTMLDivElement>(null);

  // Load Dancing Script font for pre-signed signature rendering
  useEffect(() => {
    const linkId = 'dancing-script-font';
    if (!document.getElementById(linkId)) {
      const link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  // Suppress passive event listener warnings from react-signature-canvas
  useEffect(() => {
    const wrapper = sigWrapperRef.current;
    if (!wrapper) return;
    const noop = () => { /* allow default */ };
    const opts = { passive: true } as AddEventListenerOptions;
    wrapper.addEventListener('touchstart', noop, opts);
    wrapper.addEventListener('touchmove', noop, opts);
    return () => {
      wrapper.removeEventListener('touchstart', noop);
      wrapper.removeEventListener('touchmove', noop);
    };
  }, [gateStatus]);

  // Auto-fill Red Bridge date fields in rendered NDA content
  useEffect(() => {
    const dateEls = document.querySelectorAll('.rb-auto-date');
    if (dateEls.length > 0) {
      const today = new Date().toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
      });
      dateEls.forEach(el => {
        el.textContent = today;
      });
    }
  }, [ndaContent, gateStatus]);

  useEffect(() => {
    const init = async () => {
      try {
        // Load NDA content
        const template = await publicAgreementAPI.getDemoNDA();
        if (!template || !template.content) {
          // No NDA configured — skip gate entirely
          setGateStatus('pass_through');
          return;
        }
        setNdaContent(template);

        // Check if email already signed
        if (emailParam) {
          try {
            const check = await publicAgreementAPI.checkDemoNDA(emailParam);
            if (check.signed) {
              setAlreadySigned(true);
              setGateStatus('pass_through');
              return;
            }
          } catch {
            // If check fails, still show the NDA form
          }
        }
        setGateStatus('needs_signature');
      } catch (err) {
        // On any error, don't block — show booking directly
        console.error('NDA gate error:', err);
        setGateStatus('pass_through');
      }
    };
    init();
  }, [emailParam]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!sigRef.current || sigRef.current.isEmpty()) {
      setError('Please provide your signature.');
      return;
    }

    setSubmitting(true);
    try {
      const signature = sigRef.current.toDataURL('image/png');
      await publicAgreementAPI.signDemoNDA({
        name,
        email,
        company,
        address,
        signature,
      });
      setAlreadySigned(true);
      setGateStatus('pass_through');
    } catch (err: any) {
      setError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (gateStatus === 'loading') {
    return <div className="nda-loading">Loading...</div>;
  }

  if (gateStatus === 'pass_through') {
    return (
      <>
        {alreadySigned && (
          <div className="nda-success-banner">
            NDA signed — welcome to your portal walkthrough!
          </div>
        )}
        <BookingPage />
      </>
    );
  }

  return (
    <div className="nda-gate">
      <div className="nda-header">
        <h1>Non-Disclosure Agreement</h1>
        <p>Please review and sign before your portal walkthrough</p>
      </div>

      <div className="nda-content">
        {ndaContent && (
          <div
            className="nda-scroll-box"
            dangerouslySetInnerHTML={{ __html: ndaContent.content }}
          />
        )}

        <form className="nda-form" onSubmit={handleSubmit}>
          {error && <div className="nda-error">{error}</div>}

          <div className="nda-form-row">
            <label>Full Name *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              required
              placeholder="Your full legal name"
            />
          </div>

          <div className="nda-form-row">
            <label>Email *</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              placeholder="your@email.com"
            />
          </div>

          <div className="nda-form-row">
            <label>Company</label>
            <input
              type="text"
              value={company}
              onChange={e => setCompany(e.target.value)}
              placeholder="Company or farm name"
            />
          </div>

          <div className="nda-form-row">
            <label>Address</label>
            <textarea
              value={address}
              onChange={e => setAddress(e.target.value)}
              placeholder="Mailing address"
            />
          </div>

          <div className="nda-signature-area">
            <label>Signature *</label>
            <div className="nda-sig-canvas-wrapper" ref={sigWrapperRef}>
              <SignatureCanvas
                ref={sigRef}
                canvasProps={{ className: 'nda-sig-canvas' }}
                penColor="#333"
              />
              <button
                type="button"
                className="nda-sig-clear"
                onClick={() => sigRef.current?.clear()}
              >
                Clear
              </button>
            </div>
          </div>

          <div className="nda-checkbox-row">
            <input
              type="checkbox"
              id="nda-agree"
              checked={agreed}
              onChange={e => setAgreed(e.target.checked)}
            />
            <label htmlFor="nda-agree">
              I have read and agree to the terms of this Non-Disclosure Agreement.
            </label>
          </div>

          <button
            type="submit"
            className="nda-submit-btn"
            disabled={!agreed || !name || !email || submitting}
          >
            {submitting ? 'Signing...' : 'Sign & Continue'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default BookingNDAGate;
